import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackContact } from './GoogleAnalytics';
import { usePublicFloatingCtaVisible } from '../hooks/usePublicFloatingCtaVisible';

/**
 * Facebook Messenger chat button for public pages.
 * Do not put `relative` on the same node as `fixed` — Tailwind
 * position utilities conflict and the button falls into document flow.
 */

const FB_PAGE_ID = '100086623726840';

export default function MessengerWidget() {
  const visible = usePublicFloatingCtaVisible();
  const [isHovered, setIsHovered] = useState(false);
  const [showWidget, setShowWidget] = useState(false);

  // Delay showing widget to not interrupt initial page load
  useEffect(() => {
    if (!visible) {
      setShowWidget(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowWidget(true), 2000);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!visible || !showWidget) {
    return null;
  }

  const messengerUrl = `https://m.me/${FB_PAGE_ID}`;

  return (
    <AnimatePresence>
      <div
        data-testid="messenger-dock"
        className="fixed z-50 right-[calc(1.5rem+env(safe-area-inset-right,0px))] bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))]"
      >
        {/* Inner relative box for the badge; keeps `fixed` on the dock. */}
        <motion.div
          className="relative"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.5 }}
        >
          <AnimatePresence>
            {isHovered && (
              <motion.div
                className="absolute bottom-full right-0 mb-3 bg-white rounded-2xl shadow-lg p-4 w-64"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <p className="text-gray-800 font-medium text-sm">
                  Chat with us on Messenger!
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  We typically reply within minutes
                </p>
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white rotate-45 shadow-lg" />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.a
            href={messengerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#00B2FF] to-[#006AFF] rounded-full shadow-lg hover:shadow-xl transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => trackContact('messenger')}
            aria-label="Chat with us on Facebook Messenger"
          >
            <span className="pointer-events-none absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />

            <svg
              className="w-7 h-7 text-white relative z-10"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.886 1.417 5.465 3.656 7.17V22l3.375-1.854A11.18 11.18 0 0012 20.486c5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.052 12.453l-2.545-2.715-4.97 2.715 5.467-5.802 2.608 2.715 4.906-2.715-5.466 5.802z" />
            </svg>

            <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-green-400 border-2 border-white rounded-full z-10" />
          </motion.a>

          <motion.span
            className="absolute -bottom-1 -left-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 3, type: 'spring' }}
          >
            1
          </motion.span>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
